import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../components/table/table";
import { useCommonFetch } from "../../../../hooks/useCommonFetch";
import { useEffect, useState } from "react";
import {
  createLeaveType,
  deleteLeaveType,
  updateLeaveType,
} from "../../../../services/workspace/leaveType.service";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getSalaryReport } from "../../../../services/workspace/payroll.service";
import DefaultInput from "../../../components/inputs/defaultInput";

const actions_type = {
  DELETE: "DELETE",
  ADD_EDIT: "ADD_EDIT",
};

const items = [{ label: "Generate Salary", path: "" }];

const GenerateSalary = () => {
  const [isEditValue, setIsEditValue] = useState(null);
  const { limit } = useSelector((state) => state.global);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [adjustmentAmounts, setAdjustmentAmounts] = useState({});

  const handleCheckbox = (selectedRowIds) => {
    setSelectedIds(selectedRowIds);
  };

  const handleSelectedButton = (selectedRows) => {
    // setOpenModel(true);
  };

  // Handle adjustment amount change
  const handleAdjustmentAmountChange = (userId, value) => {
    // Allow empty string, numbers, and minus sign at the beginning
    if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
      setAdjustmentAmounts(prev => ({
        ...prev,
        [userId]: value
      }));
    }
  };

  // Validate adjustment amount
  const validateAdjustmentAmount = (value) => {
    if (value === '' || value === '-') return true;
    const num = parseFloat(value);
    return !isNaN(num);
  };

  // Calculate net salary based on adjustment amount
  const calculateNetSalary = (calculatedSalary, adjustmentAmount) => {
    if (!adjustmentAmount || adjustmentAmount === '' || adjustmentAmount === '-') {
      return calculatedSalary;
    }
    
    const amount = parseFloat(adjustmentAmount);
    if (isNaN(amount)) {
      return calculatedSalary;
    }
    
    // If adjustment amount starts with minus sign, subtract it
    if (adjustmentAmount.startsWith('-')) {
      return calculatedSalary - Math.abs(amount);
    } else {
      // If no sign or positive sign, add it
      return calculatedSalary + amount;
    }
  };

  const actions = [
    {
      label: "Save",
      icon: null,
      onClick: () => handleSaveAdjustments(),
      color: "text-primary-800",
    },
    {
      label: "Reset All",
      icon: null,
      onClick: () => {
        setAdjustmentAmounts({});
        toast.success("All adjustment amounts have been reset!");
      },
      color: "text-red-600",
    },
  ];

  const {
    loading,
    data: tableData,
    total: totalData,
    currentPage,
    search,
    selectedId,
    action,
    handleSearchChange,
    handlePageChange,
    openModal,
    closeModal,
    handleCreate,
    handleUpdate,
    handleDelete,
    sortingUIProps,
  } = useCommonFetch(getSalaryReport, { 
    dataPath: "payrollAttendance.data",
    totalPath: "payrollAttendance.meta.total",
    errorMessage: "Failed to fetch leave data!",
    debounceDelay: 500,
    sortingOptions: [

      { label: "Name", field: "name" }, 
      { label: "Amount", field: "net_salary" },
    ],
    sortingModule: "payrollAttendance",
    orderParamNames: { orderByKey: "order_by", orderTypeKey: "order_type", includeDisabledKey: "includeDisabled" },
    sortingDisableText: "Disabled",
    sortingShowDisableToggle: false,

    dataTransformer: (data) => {
      if (Array.isArray(data)) {
        const transformedData = data.map((item) => {
          const userId = item.user?.uuid;
          // Start with blank adjustment amount
          const currentAdjustment = '';
          const calculatedSalary = item.calculated_salary || 0;
          const netSalary = calculateNetSalary(calculatedSalary, currentAdjustment);

          return {
            id: userId,
            name: {
              data: {
                name: item.user?.name ?? "Unknown",
                email: item.email ?? "",
                id: item.user?.code ?? "N/A",
                image: item.photo || null,
                link: item.uuid ? `/staff/${item.uuid}` : "#",
              },
              variant: "checkbox-profile",
            },

            payable_days: {
              data: item.payable_days,
              variant: "number",
            },
            allowances: {
              data: item.total_allowances,
              variant: "number",
            },
            deduction: {
              data: item.total_dedctions,
              variant: "number",
            },
            payable_salary: {
              data: item.basic_salary,
              variant: "number",
            },
            calculated_salary: {
              data: calculatedSalary,
              variant: "number",
            },
            adjustment_amount: {
              data: (
                <input
                  type="text"
                  placeholder="Enter adjustment amount (e.g., 500 or -300)"
                  value={currentAdjustment}
                  onChange={(e) => handleAdjustmentAmountChange(userId, e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                    currentAdjustment && currentAdjustment !== '-' && !validateAdjustmentAmount(currentAdjustment)
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : currentAdjustment && currentAdjustment !== '-'
                      ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                      : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                  style={{ minWidth: "120px" }}
                />
              ),
              variant: "text",
            },
            net_salary: {
              data: netSalary,
              variant: "number",
            },
            status: {
              data: item.status,
              variant: "status",
            },
          };
        });

        const actionButton = {
          delete: (item) => {
            handleDelete(item.id, deleteLeaveType, {
              successMessage: "Payroll Report deleted successfully!",
              errorMessage: "Failed to delete payroll report!",
            });
          },
          edit: (item) => {
            openModal(item, actions_type.ADD_EDIT);
            setIsEditValue({
              id: item.id,
              name: item.name.data,
              description: item.description.data,
              from_date: item.from_date.data,
              to_date: item.to_date.data,
            });
          },
        };
        return { data: transformedData, action: actionButton };
      }
      return { data: [], action: {} };
    },
  });

  // Custom state for transformed data that updates when adjustment amounts change
  const [transformedTableData, setTransformedTableData] = useState({ data: [], action: {} });

  // Update transformed data when tableData or adjustmentAmounts change
  useEffect(() => {
    if (tableData && Array.isArray(tableData.data)) {
      const updatedData = tableData.data.map((item) => {
        const userId = item.id;
        // Start with blank value, don't use the original adjustment_amount data
        const currentAdjustment = adjustmentAmounts[userId] || '';
        const calculatedSalary = item.calculated_salary?.data || 0;
        const netSalary = calculateNetSalary(calculatedSalary, currentAdjustment);
        
        return {
          ...item,
          adjustment_amount: {
            data: (
              <DefaultInput type={"text"} placeholder={"Enter Amount"} value={currentAdjustment} onChange={(e) => handleAdjustmentAmountChange(userId, e.target.value)}/>
             
            ),
            variant: "text",
          },
          net_salary: {
            ...item.net_salary,
            data: netSalary,
          },
        };
      });

      setTransformedTableData({
        data: updatedData,
        action: tableData.action
      });
    }
  }, [tableData, adjustmentAmounts]);

  // Save adjustment amounts
  const handleSaveAdjustments = () => {
    // Here you can implement the logic to save adjustment amounts to your backend
    toast.success("Adjustment amounts saved successfully!");
  };

  const TableHeaderData = [
    { label: "Staff Name", key: "name" },
    { label: "Payable Days", key: "payable_days" },
    { label: "Payable Salary", key: "payable_salary" },
    { label: "Allowances(+)", key: "allowances" },
    { label: "Gross Salary", key: "gross_salary" },
    { label: "Deduction(-)", key: "deduction" },
    { label: "Calculated Salary", key: "calculated_salary" },
    { 
      label: " Adjustment(+/-)", 
      key: "adjustment_amount" 
    },
    { label: "Net Salary", key: "net_salary" },
  ];

  const fixedcomprehensiveFields = [
    {
      name: "name",
      label: "Staff Name",
      type: "text",
      placeholder: "Enter name",
      required: true,
    },
    {
      name: "payable_days",
      label: "Payable Days",
      type: "number",
      required: true,
    },
    {
      name: "payable_salary",
      label: "Payable Salary",
      type: "number",
      required: true,
    },
    {
      name: "allowance",
      label: "Allowances(+)",
      type: "number",
      required: true,
    },
    {
      name: "gross_salary",
      label: "Gross Salary",
      type: "number",
      required: true,
    },
    {
      name: "deduction",
      label: "Deduction(-)",
      type: "number",
      required: true,
    },
    {
      name: "calculated_salary",
      label: "Calculated Salary",
      type: "number",
      required: true,
    },
    {
      name: "adjustment",
      label: "Allowances(+)",
      type: "number",
      required: true,
    },
    { name: "net_salary", label: "Net Salary", type: "number", required: true },
  ];

  
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (isEditValue) {
      await handleUpdate(values, isEditValue.id, updateLeaveType, {
        successMessage: "Payroll Report updated successfully!",
        errorMessage: "Failed to update payroll report!",
      });
    } else {
      await handleCreate(values, createLeaveType, {
        successMessage: "Payroll Report created successfully!",
        errorMessage: "Failed to create payroll report!",
      });
    }
    setSubmitting(false);
    setTimeout(() => {
      resetForm();
      setIsEditValue(null);
    }, 1000);
  };

  const handleCancel = () => {
    setIsEditValue(null);
    closeModal();
  };

  return (
    <div className="flex flex-col gap-2">
      <PageTableHeader
        isSearch
        onSearchChange={handleSearchChange}
        filter={false}
        shorting
        sortingModule="payrollAttendance"
        onSortingChange={sortingUIProps?.onSortingChange}
        sortingProps={sortingUIProps}
        breadcrumbs={items}
      />

      <div className="bg-white p-4 rounded-md">
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            <strong>Adjustment Amount Instructions:</strong> Enter positive amounts to add to salary (e.g., 500) or negative amounts to subtract (e.g., -300). 
            The net salary will be automatically calculated as: Calculated Salary ± Adjustment Amount.
          </p>
        </div>
        
        <CommonTable
        search={search}
        TableHeaderData={TableHeaderData}
        tabledata={transformedTableData.data}
        currentPage={currentPage}
        totalData={totalData}
        setCurrentPage={handlePageChange}
        loading={loading}
        action={transformedTableData.action}
        selectionKey="id"
        selectedRows={selectedIds}
        onSelectionChange={setSelectedIds}
        actions={actions}
      />

      {/* Summary Section */}
      {/* <div className="mt-4 p-4 bg-gray-50 rounded-md">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Payroll Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-3 rounded-md border">
            <p className="text-sm text-gray-600">Total Staff</p>
            <p className="text-xl font-semibold text-gray-800">{transformedTableData.data?.length || 0}</p>
          </div>
          <div className="bg-white p-3 rounded-md border">
            <p className="text-sm text-gray-600">Total Adjustments</p>
            <p className={`text-xl font-semibold ${
              Object.values(adjustmentAmounts).reduce((sum, val) => {
                const num = parseFloat(val) || 0;
                return sum + num;
              }, 0) >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {Object.values(adjustmentAmounts).reduce((sum, val) => {
                const num = parseFloat(val) || 0;
                return sum + num;
              }, 0).toFixed(2)}
            </p>
          </div>
          <div className="bg-white p-3 rounded-md border">
            <p className="text-sm text-gray-600">Total Net Salary</p>
            <p className="text-xl font-semibold text-gray-800">
              {(transformedTableData.data?.reduce((sum, item) => {
                const calculatedSalary = item.calculated_salary?.data || 0;
                const adjustment = adjustmentAmounts[item.id] || 0;
                const netSalary = calculateNetSalary(calculatedSalary, adjustment);
                return sum + netSalary;
              }, 0) || 0).toFixed(2)}
            </p>
          </div>
        </div>
      </div> */}
      </div>
    </div>
  );
};

export default GenerateSalary;
